import { Stack } from "@mui/material";
import { useEffect, useRef } from "react";
import {
  PrimaryLoginPassword,
  PrimaryLoginUsername,
} from "react-common-ui-library";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  EMPTY,
  FORGOT_PASSWORD_ROUTE,
  FORGOT_USERNAME_ROUTE,
  IS_RECAPTCHA_ENABLED,
  MODE_ALL,
} from "../../Constants/CommonConstants";
import {
  LOGIN_FAILED,
  LOGIN_PASSWORD,
  LOGIN_PASSWORD_REQUIRED_ERROR,
  LOGIN_USERNAME,
  LOGIN_USERNAME_REQUIRED_ERROR,
  MAX_LOGIN_FIELD_LENGTH,
} from "../../Constants/LoginConstants";
import { MANUAL } from "../../Constants/VerifyAccountConstants";
import {
  clearLoginApiResponse,
  clearLoginSlice,
  postAuthenticate,
} from "../../Features/LoginSlice";
import GoogleReCaptcha from "../Application/GoogleReCaptcha";
import LoginButton from "./LoginButton";
import { LoginFailedErrorMessage } from "./LoginFailedErrorMessage";
import { manageApiResponseSelector } from "../../Selectors/ApiResponseSelector";
import {
  SLICE_LOGIN,
  THUNK_POST_AUTHENTICATE,
} from "../../Constants/SliceAndThunkConstants";
import ConfigService from "../../Services/ConfigService";

const isReCaptchaEnabled = ConfigService(IS_RECAPTCHA_ENABLED);

/**
 * @description Displays the Login Input Form
 * @returns {ReactNode} Renders the Login Input form to the user
 */
const LoginForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const methods = useForm({
    mode: MODE_ALL,
  });

  const loginRef = useRef();

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(state, SLICE_LOGIN, THUNK_POST_AUTHENTICATE)
  );

  const { isFailure, description } = apiResponses;

  const {
    formState: { errors },
  } = methods;
  const formError =
    errors?.userName?.type === MANUAL || errors?.loginPassword?.type === MANUAL;

  const forgotUsernameLinkClickHandler = () => {
    dispatch(clearLoginSlice());
    navigate(`/${FORGOT_USERNAME_ROUTE}`);
  };

  const forgotPasswordLinkClickHandler = () => {
    dispatch(clearLoginSlice());
    navigate(`/${FORGOT_PASSWORD_ROUTE}`);
  };

  const handleLogin = async (data) => {
    const token = isReCaptchaEnabled
      ? await loginRef.current.onSubmitWithReCAPTCHA()
      : null;
    if (token || !isReCaptchaEnabled) {
      const payload = {
        userName: data.userName,
        password: data.loginPassword,
        loginRecaptchaToken: token,
      };
      dispatch(postAuthenticate(payload));
    }
  };

  useEffect(() => {
    if (isFailure && description === LOGIN_FAILED) {
      methods.reset({ userName: EMPTY, loginPassword: EMPTY });
      methods.setError(LOGIN_USERNAME, {
        type: MANUAL,
      });
      methods.setError(LOGIN_PASSWORD, {
        type: MANUAL,
      });
      if(isReCaptchaEnabled) {
        loginRef.current.onResetReCAPTCHA()
      }
    }
    dispatch(clearLoginApiResponse(THUNK_POST_AUTHENTICATE));
  }, [description, dispatch, isFailure, methods]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleLogin)}>
        <Stack spacing={{ xs: 7, md: 4 }}>
          <Stack spacing={3}>
            <PrimaryLoginUsername
              formProps={{
                rules: {
                  required: {
                    value: true,
                    message: LOGIN_USERNAME_REQUIRED_ERROR,
                  },
                },
              }}
              fieldProps={{
                restrictionPattern: MAX_LOGIN_FIELD_LENGTH,
              }}
              linkClick={forgotUsernameLinkClickHandler}
            />
            <PrimaryLoginPassword
              formProps={{
                rules: {
                  required: {
                    value: true,
                    message: LOGIN_PASSWORD_REQUIRED_ERROR,
                  },
                },
              }}
              fieldProps={{
                restrictionPattern: MAX_LOGIN_FIELD_LENGTH,
              }}
              linkClick={forgotPasswordLinkClickHandler}
            />
          </Stack>
          {formError && <LoginFailedErrorMessage />}
          <LoginButton />
          {isReCaptchaEnabled ? <GoogleReCaptcha ref={loginRef} /> : null}
        </Stack>
      </form>
    </FormProvider>
  );
};

export default LoginForm;
